import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Building2, Users, Layers, Mail, BookOpen, UserPlus, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { dashboardApi, type DashboardStats } from '@/lib/api/admin-dashboard';
import { adminApi, type OrgSummary } from '@/lib/api/admin';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { formatTimestamp } from '@/lib/date';

export const Route = createFileRoute('/admin/dashboard')({
  component: DashboardPage,
});

interface KpiCardProps {
  title: string;
  value: number | string;
  deltaText?: string;
  deltaUp?: boolean;
  icon: React.ReactNode;
  iconBg: string;
  valueColor: string;
  to: string;
  search?: Record<string, string>;
}

function KpiCard({ title, value, deltaText, deltaUp, icon, iconBg, valueColor, to, search }: KpiCardProps) {
  return (
    <Link
      to={to as never}
      search={search as never}
      className="block"
    >
      <Card className="cursor-pointer transition-shadow hover:shadow-md">
        <CardContent className="p-5">
          <div className="mb-4 flex items-center gap-3">
            <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${iconBg}`}>
              {icon}
            </div>
            <span className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80">
              {title}
            </span>
          </div>
          <div className="flex items-end gap-2">
            <span className={`text-5xl font-bold leading-none ${valueColor}`}>{value}</span>
            {deltaText && (
              <div className={`mb-1 flex items-center gap-0.5 text-[12px] font-medium ${deltaUp ? 'text-emerald-500' : 'text-muted-foreground'}`}>
                {deltaUp && <TrendingUp className="h-3.5 w-3.5" />}
                <span>{deltaText}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

const QUICK_ACTIONS = [
  { label: 'New Organization', to: '/admin/orgs/new', icon: <Building2 className="h-4 w-4" /> },
  { label: 'New Batch', to: '/admin/batches', icon: <Layers className="h-4 w-4" /> },
  { label: 'New Curriculum', to: '/admin/curriculum', icon: <BookOpen className="h-4 w-4" /> },
  { label: 'Add User', to: '/admin/users', icon: <UserPlus className="h-4 w-4" /> },
];

function DashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [orgs, setOrgs] = useState<OrgSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([dashboardApi.getStats(), adminApi.listOrgs()])
      .then(([s, o]) => { setStats(s); setOrgs(o.slice(0, 5)); })
      .catch(() => toast.error('Failed to load dashboard data'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-5">
      {/* KPI row */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard
          title="Organizations"
          icon={<Building2 className="h-5 w-5 text-orange-500" />}
          iconBg="bg-orange-50"
          value={loading ? '—' : (stats?.total_orgs ?? 0)}
          valueColor="text-orange-500"
          deltaText={stats?.orgs_delta_30d ? `this month` : 'this month'}
          deltaUp={!!stats?.orgs_delta_30d && stats.orgs_delta_30d > 0}
          to="/admin/orgs"
        />
        <KpiCard
          title="Users"
          icon={<Users className="h-5 w-5 text-blue-500" />}
          iconBg="bg-blue-50"
          value={loading ? '—' : (stats?.total_users ?? 0)}
          valueColor="text-blue-500"
          deltaText="this week"
          deltaUp={!!stats?.users_delta_7d && stats.users_delta_7d > 0}
          to="/admin/users"
        />
        <KpiCard
          title="Active Batches"
          icon={<Layers className="h-5 w-5 text-emerald-600" />}
          iconBg="bg-emerald-50"
          value={loading ? '—' : (stats?.active_batches ?? 0)}
          valueColor="text-emerald-600"
          deltaText="running now"
          to="/admin/batches"
          search={{ status: 'active' }}
        />
        <KpiCard
          title="Pending Invites"
          icon={<Mail className="h-5 w-5 text-violet-700" />}
          iconBg="bg-violet-50"
          value={loading ? '—' : (stats?.pending_invites ?? 0)}
          valueColor="text-violet-700"
          deltaText={(stats?.pending_invites ?? 0) > 0 ? 'need action' : 'all clear'}
          to="/admin/users"
          search={{ status: 'invited' }}
        />
      </div>

      {/* Content grid */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_260px]">
        {/* Organizations table */}
        <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <h2 className="text-[14px] font-semibold text-foreground">Organizations</h2>
            <Link to="/admin/orgs" className="text-[12px] font-medium text-primary hover:underline">
              View all →
            </Link>
          </div>
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="px-5 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Name</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Batches</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Members</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Pending</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/50">
              {loading ? (
                [1, 2, 3, 4].map((i) => (
                  <tr key={i}>
                    <td className="px-5 py-3"><Skeleton className="h-4 w-36" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-8" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-8" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-8" /></td>
                  </tr>
                ))
              ) : orgs.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-5 py-10 text-center text-sm text-muted-foreground">
                    No organizations yet.{' '}
                    <Link to="/admin/orgs/new" className="text-primary hover:underline">Create one</Link>
                  </td>
                </tr>
              ) : (
                orgs.map((o) => (
                  <tr key={o.id} className="hover:bg-muted/20">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-orange-50">
                          <Building2 className="h-3.5 w-3.5 text-orange-500" />
                        </div>
                        <Link
                          to="/admin/orgs/$orgId"
                          params={{ orgId: o.id }}
                          className="text-[13px] font-medium text-foreground hover:text-primary"
                        >
                          {o.name}
                        </Link>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[13px] text-muted-foreground">{o.batch_count ?? 0}</td>
                    <td className="px-4 py-3 text-[13px] text-muted-foreground">{o.member_count}</td>
                    <td className="px-4 py-3">
                      {o.pending_count > 0 ? (
                        <span className="inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                          {o.pending_count}
                        </span>
                      ) : (
                        <span className="text-[12px] text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Right column */}
        <div className="space-y-4">
          {/* Quick Actions */}
          <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
            <div className="border-b border-border px-4 py-3.5">
              <h2 className="text-[13px] font-semibold text-foreground">Quick Actions</h2>
            </div>
            <div className="space-y-2 p-3">
              {QUICK_ACTIONS.map((a) => (
                <Link
                  key={a.to}
                  to={a.to as never}
                  className="flex w-full items-center gap-2.5 rounded-lg px-4 py-2.5 text-[13px] font-medium text-white transition-colors"
                  style={{ backgroundColor: 'hsl(22 93% 46%)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'hsl(22 93% 40%)')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'hsl(22 93% 46%)')}
                >
                  {a.icon}
                  {a.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
            <div className="border-b border-border px-4 py-3.5">
              <h2 className="text-[13px] font-semibold text-foreground">Recent Activity</h2>
            </div>
            <ul className="divide-y divide-border/50">
              {loading ? (
                [1, 2, 3].map((i) => (
                  <li key={i} className="flex items-center gap-3 px-4 py-3">
                    <Skeleton className="h-7 w-7 rounded-full" />
                    <div className="flex-1 space-y-1.5">
                      <Skeleton className="h-3 w-3/4" />
                      <Skeleton className="h-2.5 w-1/3" />
                    </div>
                  </li>
                ))
              ) : orgs.length === 0 ? (
                <li className="px-4 py-6 text-center text-xs text-muted-foreground">No activity yet.</li>
              ) : (
                orgs.map((o) => (
                  <li key={o.id} className="flex items-start gap-3 px-4 py-3">
                    <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-orange-50">
                      <Building2 className="h-3.5 w-3.5 text-orange-500" />
                    </div>
                    <div>
                      <p className="text-[12px] text-foreground">
                        <span className="font-semibold">{o.name}</span> was created
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {formatTimestamp(o.created_at)}
                      </p>
                    </div>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
